import React, { useMemo } from "react"
import clsx from "clsx"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { buttonVariants } from "@react/shared/ui/Button"
import { t, fmtHours, weekdayShort, dayNumber } from "../helpers/format"
import { dayKey, isApprovable } from "../helpers/states"
import GridRow from "./GridRow"
import Checkbox from "./Checkbox"

export const NAME_WIDTH  = 236
export const TOTAL_WIDTH = 132

export const columnsFor = (count, mode) =>
  `${NAME_WIDTH}px repeat(${count}, minmax(${mode === "month" ? 58 : 108}px, 1fr)) ${TOTAL_WIDTH}px`

const DayHeader = ({ day, mode }) => (
  <div
    className = {clsx(
      "sticky top-0 z-20 flex flex-col items-center justify-center gap-0.5 py-2 border-b border-slate-200",
      day.today ? "bg-brand-50" : day.weekend || day.holiday ? "bg-slate-50" : "bg-white"
    )}
    title = {day.holiday || undefined}
  >
    <span className={clsx("text-[10.5px] uppercase tracking-wider font-medium", day.today ? "text-brand-600" : "text-slate-400")}>
      {mode === "month" ? weekdayShort(day.date).slice(0, 2) : weekdayShort(day.date)}
    </span>
    <span
      className = {clsx(
        "inline-grid place-items-center min-w-[26px] h-[26px] px-1 rounded-full text-[13px] font-semibold tabular-nums",
        day.today ? "bg-brand-500 text-white" : day.weekend ? "text-slate-400" : "text-slate-800"
      )}
    >
      {dayNumber(day.date)}
    </span>
    {day.holiday && mode === "week" && (
      <span className="max-w-full px-1 truncate text-[10px] font-medium text-amber-600">{day.holiday}</span>
    )}
    {day.holiday && mode === "month" && <span className="w-[5px] h-[5px] rounded-full bg-amber-500" />}
  </div>
)

const Grid = ({ board, mode, selected, flash, active, planningUrl, onToggle, onOpen, onApproveRow, busy }) => {
  const { days, rows } = board

  const allKeys = useMemo(() => rows.flatMap(row =>
    Object.entries(row.cells).filter(([, cell]) => isApprovable(cell)).map(([date]) => dayKey(row.user.id, date))
  ), [rows])

  const dayTotals = useMemo(() => days.map(day =>
    rows.reduce((sum, row) => sum + (row.cells[day.date]?.hours || 0), 0)
  ), [days, rows])

  const grandTotal = dayTotals.reduce((sum, value) => sum + value, 0)
  const allChecked = allKeys.length > 0 && allKeys.every(key => selected.has(key))
  const someChecked = allKeys.some(key => selected.has(key))

  if (!rows.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl py-16 flex flex-col items-center text-center">
        <span className="inline-grid place-items-center w-[52px] h-[52px] rounded-2xl bg-slate-100 text-slate-400 text-[22px] mb-4">
          <FontAwesomeIcon icon="user-clock" />
        </span>
        <div className="text-[15px] font-semibold text-slate-800">{t("empty.title")}</div>
        <p className="text-[12.5px] text-slate-500 mt-1 max-w-[380px]">{t("empty.hint")}</p>
        <a href={planningUrl} className={clsx(buttonVariants({ variant: "secondary", size: "sm" }), "mt-4")}>
          <FontAwesomeIcon icon="calendar-days" className="text-slate-400" />
          {t("actions.open_planning")}
        </a>
      </div>
    )
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-auto max-h-[calc(100vh-170px)]">
      <div
        className = "grid"
        style     = {{ gridTemplateColumns: columnsFor(days.length, mode) }}
        role      = "grid"
      >
        <div className="sticky top-0 left-0 z-30 flex items-center gap-2.5 px-3 bg-white border-b border-r border-slate-200">
          <Checkbox
            checked       = {allChecked}
            indeterminate = {!allChecked && someChecked}
            disabled      = {!allKeys.length}
            onChange      = {on => onToggle(allKeys, on)}
            label         = {t("grid.select_all")}
          />
          <span className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500">
            {t("grid.people", { count: rows.length })}
          </span>
        </div>
        {days.map(day => <DayHeader key={day.date} day={day} mode={mode} />)}
        <div className="sticky top-0 z-20 flex items-center justify-end px-3 bg-white border-b border-l border-slate-200 text-[10.5px] uppercase tracking-wider font-semibold text-slate-500">
          {t("grid.total")}
        </div>

        {rows.map(row => (
          <GridRow
            key          = {row.user.id}
            row          = {row}
            days         = {days}
            mode         = {mode}
            selected     = {selected}
            flash        = {flash}
            active       = {active}
            busy         = {busy}
            onToggle     = {onToggle}
            onOpen       = {onOpen}
            onApproveRow = {onApproveRow}
          />
        ))}

        <div className="sticky bottom-0 left-0 z-30 flex items-center px-3 h-[40px] bg-slate-50 border-t border-r border-slate-200 text-[11px] uppercase tracking-wider font-semibold text-slate-500">
          {t("grid.day_total")}
        </div>
        {days.map((day, index) => (
          <div key={day.date} className="sticky bottom-0 z-20 flex items-center justify-center h-[40px] bg-slate-50 border-t border-slate-200 text-[12px] font-semibold tabular-nums text-slate-700">
            {dayTotals[index] ? fmtHours(dayTotals[index]) : <span className="text-slate-300">–</span>}
          </div>
        ))}
        <div className="sticky bottom-0 z-20 flex items-center justify-end px-3 h-[40px] bg-slate-50 border-t border-l border-slate-200 text-[13px] font-bold tabular-nums text-slate-900">
          {fmtHours(grandTotal)}
        </div>
      </div>
    </div>
  )
}

export default Grid
